import { useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { useCallback } from 'react'
import { useAssetUpload } from '@opallabs/api'
import type { RichTextDocument } from '../queries'
import { queryKeys as textEditorQueryKeys } from '../queries/queryKeys'
import { TextEditorAsset } from '../types'
import { transformAssetReferenceToTextEditorAsset } from '../utils'

/**
 * Asset upload handler for text editor documents that handles file uploads and cache updates.
 */
export const useDocumentAssetUpload = ({
  documentId,
  resourceType,
}: {
  documentId: string
  resourceType?: string
}) => {
  const queryClient = useQueryClient()
  const assetUpload = useAssetUpload({ entityResourceType: resourceType })

  return useCallback(
    async (files: File[]): Promise<(TextEditorAsset | null)[]> => {
      const responses = await Promise.allSettled(
        files.map((file) =>
          assetUpload.mutateAsync({ entityId: documentId, file })
        )
      )

      const succeeded = responses.filter((r) => r.status === 'fulfilled')

      queryClient.setQueryData<RichTextDocument>(
        textEditorQueryKeys.document(documentId),
        (cachedDocument) =>
          produce(cachedDocument, (draft) => {
            if (!draft?.included) return

            succeeded.forEach((r) => {
              const asset = r.value.asset.data
              draft.included?.push({
                ...asset,
                links: {
                  v2_request: '#',
                },
              })
            })
          })
      )

      return responses.map((r) =>
        r.status === 'fulfilled'
          ? transformAssetReferenceToTextEditorAsset(
              r.value.assetReference.data.attributes
            )
          : null
      )
    },
    [assetUpload, documentId, queryClient]
  )
}
